import { useMemo } from 'react'
import { collection, doc, query, where } from 'firebase/firestore'
import type { DocumentReference, Query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { FirebaseError } from 'firebase/app'
import { fbt } from 'fbtee'
import { db, functions } from '../lib/firebase'
import { useFirestoreDoc, useFirestoreCollection } from './useFirestore'
import { useBlockedUsers } from '../contexts/BlockedUsersContext'
import { runMutation } from '../lib/firestoreMutation'
import { toastError } from '../lib/toast'
import { track } from '../lib/analytics'
import {
  eventTicketsId,
  type RespondToTicketRequest, type RespondToTicketResult, type SaveEventTicketsRequest,
  type SaveEventTicketsResult, type TicketResponse,
} from '@shared/eventTickets'
import type { EventTickets } from '../types'

const callSave = httpsCallable<SaveEventTicketsRequest, SaveEventTicketsResult>(functions, 'saveEventTickets')
const callRespond = httpsCallable<RespondToTicketRequest, RespondToTicketResult>(functions, 'respondToTicket')

const EMPTY_SET: ReadonlySet<string> = new Set()
const EMPTY_MAP: ReadonlyMap<string, string> = new Map()

export interface EventTicketsView {
  mine: EventTickets | null
  /** Ledgers others own that the viewer can read, minus blocked owners. */
  others: EventTickets[]
  /** Faro users with an accepted ticket in any visible ledger (incl. the viewer's own 'me' slot), minus blocked users. */
  holderIds: ReadonlySet<string>
  /** holderIds ∩ (peopleScopeIds ∪ self): the holders PeopleSection may add. Empty when peopleScopeIds is null. */
  peopleHolderIds: ReadonlySet<string>
  /** Assignee → who bought their ticket, for assignees still to confirm, scoped like peopleHolderIds: a tentative "probably going". */
  peoplePendingFrom: ReadonlyMap<string, string>
  /** Every uid named in a visible ledger (owners, holders, pending): for profile loading. */
  peopleIds: string[]
  loading: boolean
}

/**
 * The viewer's own ticket ledger for an event plus the ones their crew keep.
 * `peopleScopeIds` is who the people list shows (null under `?friend=` scope).
 */
export function useEventTickets(eventId: string, uid: string | null, peopleScopeIds: string[] | null): EventTicketsView {
  const mineRef = useMemo(
    () => (uid ? (doc(db, 'eventTickets', eventTicketsId(uid, eventId)) as DocumentReference<EventTickets>) : null),
    [uid, eventId],
  )
  const othersRef = useMemo(
    () => uid
      ? (query(
          collection(db, 'eventTickets'),
          where('eventId', '==', eventId),
          where('viewerIds', 'array-contains', uid),
        ) as Query<EventTickets>)
      : null,
    [uid, eventId],
  )
  const mine = useFirestoreDoc<EventTickets>(mineRef)
  const othersQ = useFirestoreCollection<EventTickets>(othersRef)
  const { isBlocked } = useBlockedUsers()

  const others = useMemo(
    () => othersQ.data.filter(l => l.userId !== uid && !isBlocked(l.userId)),
    [othersQ.data, isBlocked, uid],
  )

  const derived = useMemo(() => {
    const ledgers = mine.data ? [mine.data, ...others] : others
    const holders = new Set<string>()
    const pending = new Map<string, string>()
    const people = new Set<string>()
    for (const l of ledgers) {
      people.add(l.userId)
      for (const id of l.holderIds ?? []) if (!isBlocked(id)) holders.add(id)
      for (const id of l.pendingIds ?? []) if (!isBlocked(id)) pending.set(id, l.userId)
      for (const id of [...(l.holderIds ?? []), ...(l.pendingIds ?? [])]) people.add(id)
    }
    let peopleHolders = EMPTY_SET
    let peoplePending: ReadonlyMap<string, string> = EMPTY_MAP
    if (peopleScopeIds) {
      const scope = new Set(peopleScopeIds)
      if (uid) scope.add(uid)
      peopleHolders = new Set([...holders].filter(id => scope.has(id)))
      peoplePending = new Map([...pending].filter(([id]) => scope.has(id) && !holders.has(id)))
    }
    return { holderIds: holders, peopleHolderIds: peopleHolders, peoplePendingFrom: peoplePending, peopleIds: [...people].sort() }
  }, [mine.data, others, isBlocked, peopleScopeIds, uid])

  return { mine: mine.data, others, ...derived, loading: mine.loading || othersQ.loading }
}

const callableCode = (err: unknown) => (err instanceof FirebaseError ? err.code : null)

/** Owner edits. Resolves null when the save failed (already toasted). */
export async function saveEventTickets(request: SaveEventTicketsRequest): Promise<SaveEventTicketsResult | null> {
  try {
    return await runMutation(fbt("Couldn't save your tickets", 'Error toast when saving the tickets someone bought for an event fails'), async () => {
      let result: SaveEventTicketsResult
      try {
        result = (await callSave(request)).data
      } catch (err) {
        if (callableCode(err) !== 'functions/aborted') throw err
        toastError(
          fbt("Couldn't save your tickets", 'Error toast when saving the tickets someone bought for an event fails'),
          fbt(
            'Your tickets changed while you were editing — check them and save again',
            'Error toast detail when someone claimed or answered a ticket while the owner was editing their tickets',
          ),
        )
        return null
      }
      if (request.tickets) {
        track('tickets_saved', { event_id: request.eventId, kind: result.deleted ? 'cleared' : result.created ? 'created' : 'updated' })
      }
      return result
    })
  } catch {
    return null
  }
}

/** Flag (or clear) "looking for a ticket" — never sends slots or audience, so it can't undo a concurrent claim or a chosen audience. */
export async function setTicketSeeking(eventId: string, seeking: boolean): Promise<boolean> {
  const result = await saveEventTickets({ eventId, seeking })
  if (result) track('ticket_seeking_set', { event_id: eventId, kind: seeking ? 'on' : 'off' })
  return !!result
}

/** Hand the owner's first open ticket to `uid` (pending until they confirm). */
export async function giveOpenTicket(mine: EventTickets, uid: string): Promise<boolean> {
  const open = mine.tickets.find(s => s.kind === 'open')
  if (!open) return false
  const result = await saveEventTickets({
    eventId: mine.eventId,
    tickets: mine.tickets.map(s => (s.id === open.id ? { id: s.id, kind: 'user' as const, userId: uid } : s)),
    version: mine.version ?? 0,
  })
  return !!result
}

/** Claim, accept, decline or give back a slot on someone else's ledger. */
export async function respondToTicket(ledger: EventTickets, slotId: string, action: TicketResponse): Promise<boolean> {
  try {
    return await runMutation(fbt("Couldn't update the ticket", 'Error toast when claiming or answering a ticket fails'), async () => {
      try {
        await callRespond({ ledgerId: ledger.id, slotId, action })
      } catch (err) {
        const taken = callableCode(err) === 'functions/failed-precondition' &&
          (err as FirebaseError & { details?: { reason?: string } }).details?.reason === 'taken'
        if (!taken) throw err
        toastError(
          fbt('Someone beat you to that ticket', 'Error toast when another person claimed an open ticket first'),
          fbt('Try another open ticket.', 'Error toast detail when another person claimed an open ticket first'),
        )
        return false
      }
      track('ticket_responded', { event_id: ledger.eventId, kind: action })
      return true
    })
  } catch {
    return false
  }
}
